import { memo } from 'react';
import PropTypes from 'prop-types';
import { CloseOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/CloseOutlined';
import qs from 'qs';
import classnames from 'classnames';
import ClearFilters from '../clear-filters/ClearFilters';
import styles from './AppliedFilters.module.scss';
import StatefulLink, { defaultFilter } from '../../stateful-link/StatefulLink';
import { getAriaTextIfPriceRange } from '../../../utils/index';
import { useFacetAnalyticsEvent } from '../facet-context';
import { getNumberOfFilters } from './helpers';
import { useAppliedFiltersData } from './data/useAppliedFiltersData';

export const AppliedFilter = ({
  query,
  label,
  name,
  facetName,
  facetId,
  trackingId,
}) => {
  const triggerAnalyticsEvent = useFacetAnalyticsEvent({
    value: {
      facetName,
      label,
      name,
      selected: true,
      trackingId,
    },
  });

  const triggerEvents = () => {
    triggerAnalyticsEvent();
  };

  let queryParams = query;

  const queryOpts = { filter: defaultFilter() };

  if (typeof queryParams === 'string') {
    queryParams = qs.parse(queryParams);
    queryOpts.filter.push(...Object.keys(queryParams));
  }

  const ariaTextForPriceRange = getAriaTextIfPriceRange(name);

  return (
    <StatefulLink
      query={{
        ...queryParams,
        page: undefined,
      }}
      queryOpts={queryOpts}
      facetId={facetId}
      className={styles.filterLink}
      onClick={() => triggerEvents()}
      aria-label={`'${ariaTextForPriceRange || name}' filter Remove`}
      data-testid="applied-filter__link"
    >
      <span className={styles.filterLinkText}>{name}</span>
      <div className={styles.filterLinkIcon}>
        <CloseOutlined title="Remove filter" size="medium" />
      </div>
    </StatefulLink>
  );
};

const AppliedFilters = ({
  baseFacetId = '',
  className,
  grid = false,
  isFilterDrawer = false,
  mobile = false,
  numberOfFiltersToShowClear,
  showClearFiltersLink = true,
  showHeader = true,
  showHideOutOfStockFilter = true,
}) => {
  const { filter } = useAppliedFiltersData();

  if (!filter || !Object.keys(filter).length || (mobile && isFilterDrawer)) {
    return '';
  }

  const numberOfFilters = getNumberOfFilters(filter);

  const showClearFilters = numberOfFiltersToShowClear <= numberOfFilters;

  Object.values(filter).forEach((filters) =>
    filters.sort((filterA, filterB) =>
      filterA.name.toLowerCase().localeCompare(filterB.name.toLowerCase())
    )
  );

  const listOfAppliedFilters = [].concat(...Object.values(filter));

  return (
    <div
      className={classnames(className, styles.container, {
        [styles.grid]: grid,
        [styles.breadbox]: true,
        [styles.oos_filter]: showHideOutOfStockFilter,
      })}
    >
      {!mobile && showHeader ? (
        <h3 className={styles.header}>Applied Filters</h3>
      ) : (
        ''
      )}

      <ul className={styles.filters} data-testid="applied-filters">
        {listOfAppliedFilters.map((value) => (
          <li
            key={value.name}
            className={classnames(styles.filter, styles.breadbox)}
          >
            <AppliedFilter {...value} mobile={mobile} />
          </li>
        ))}
        {showClearFiltersLink && (
          <ClearFilters
            baseFacetId={baseFacetId}
            mobile={mobile}
            query={{ page: undefined }}
            show={showClearFilters}
          />
        )}
      </ul>
    </div>
  );
};

AppliedFilters.propTypes = {
  baseFacetId: PropTypes.string,
  className: PropTypes.string,
  grid: PropTypes.bool,
  isFilterDrawer: PropTypes.bool,
  mobile: PropTypes.bool,
  numberOfFiltersToShowClear: PropTypes.number.isRequired,
  showClearFiltersLink: PropTypes.bool,
  showHeader: PropTypes.bool,
  showHideOutOfStockFilter: PropTypes.bool,
};

export default memo(AppliedFilters);
